/**
 * Usage limits API (B2.1 / B2.4).
 */
import { get, put, del } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';

export type UsageLimitScope =
  | 'tenant'
  | 'agent'
  | 'session_default'
  | 'visitor';
export type UsageLimitPeriod = 'daily' | 'monthly' | 'session';
export type UsageLimitMetric = 'total_tokens' | 'estimated_cost_usd';
export type UsageLimitPool = 'chat' | 'ingest';

export interface UsageLimitRecord {
  id: string;
  tenantId: string;
  agentId: string | null;
  scope: UsageLimitScope | string;
  period: UsageLimitPeriod | string;
  metric: UsageLimitMetric | string;
  pool: UsageLimitPool | string;
  limitValue: number;
  enforcement: string;
  setBy: 'platform' | 'tenant' | string;
  isCeiling: boolean;
  warnThresholdPct: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpsertUsageLimitPayload {
  agentId?: string;
  scope: UsageLimitScope;
  period: UsageLimitPeriod;
  metric: UsageLimitMetric;
  pool: UsageLimitPool;
  limitValue: number;
  warnThresholdPct?: number;
}

export interface PlatformUpsertUsageLimitPayload extends UpsertUsageLimitPayload {
  tenantId: string;
  isCeiling?: boolean;
}

export interface UsageLimitDimensionStatus {
  scope: string;
  period: string;
  metric: string;
  pool: string;
  agentId: string | null;
  limitValue: number;
  used: number;
  reserve: number;
  remaining: number;
  warnThresholdPct: number;
  warning: boolean;
  exceeded: boolean;
  code: string;
  setByEffective: 'platform' | 'tenant' | 'both' | string;
}

export interface UsageLimitsStatusResult {
  tenantId: string;
  agentId: string | null;
  pool: string;
  now: string;
  utcDayStart: string;
  utcMonthStart: string;
  dimensions: UsageLimitDimensionStatus[];
  warning: boolean;
  allowed: boolean;
}

export interface EndUserLimitHealthRow {
  scope: 'session_default' | 'visitor' | string;
  period: 'session' | 'daily' | 'monthly' | string;
  metric: string;
  pool: string;
  agentId: string | null;
  limitValue: number;
  warnThresholdPct: number;
  setByEffective: 'platform' | 'tenant' | 'both' | string;
  withUsage: number;
  nearLimit: number;
  atOrOverLimit: number;
}

export interface EndUserLimitHealthResult {
  tenantId: string;
  agentId: string | null;
  now: string;
  utcDayStart: string;
  utcMonthStart: string;
  rows: EndUserLimitHealthRow[];
}

export function matchUsageLimit(
  rows: UsageLimitRecord[],
  match: {
    scope: string;
    period: string;
    metric: string;
    pool: string;
    agentId?: string | null;
    setBy?: string;
    isCeiling?: boolean;
  },
): UsageLimitRecord | null {
  return (
    rows.find((r) => {
      if (r.scope !== match.scope) return false;
      if (r.period !== match.period) return false;
      if (r.metric !== match.metric) return false;
      if (r.pool !== match.pool) return false;
      const wantAgent = match.agentId ?? null;
      if ((r.agentId ?? null) !== wantAgent) return false;
      if (match.setBy && r.setBy !== match.setBy) return false;
      if (match.isCeiling !== undefined && r.isCeiling !== match.isCeiling) {
        return false;
      }
      return true;
    }) ?? null
  );
}

export const usageLimitsService = {
  list(): Promise<UsageLimitRecord[]> {
    return get<UsageLimitRecord[]>(endpoints.usageLimits.list);
  },

  upsert(payload: UpsertUsageLimitPayload): Promise<UsageLimitRecord> {
    return put<UsageLimitRecord>(endpoints.usageLimits.upsert, payload);
  },

  remove(id: string): Promise<void> {
    return del(endpoints.usageLimits.remove(id));
  },

  status(params?: {
    agentId?: string;
    pool?: UsageLimitPool;
  }): Promise<UsageLimitsStatusResult> {
    const q = new URLSearchParams();
    if (params?.agentId) q.set('agentId', params.agentId);
    if (params?.pool) q.set('pool', params.pool);
    const qs = q.toString();
    return get<UsageLimitsStatusResult>(
      qs ? `${endpoints.usageLimits.status}?${qs}` : endpoints.usageLimits.status,
    );
  },

  endUserHealth(params?: {
    agentId?: string;
  }): Promise<EndUserLimitHealthResult> {
    const q = new URLSearchParams();
    if (params?.agentId) q.set('agentId', params.agentId);
    const qs = q.toString();
    return get<EndUserLimitHealthResult>(
      qs
        ? `${endpoints.usageLimits.endUserHealth}?${qs}`
        : endpoints.usageLimits.endUserHealth,
    );
  },

  listPlatform(tenantId?: string): Promise<UsageLimitRecord[]> {
    const qs = tenantId
      ? `?tenantId=${encodeURIComponent(tenantId)}`
      : '';
    return get<UsageLimitRecord[]>(
      `${endpoints.platformUsageLimits.list}${qs}`,
    );
  },

  upsertPlatform(
    payload: PlatformUpsertUsageLimitPayload,
  ): Promise<UsageLimitRecord> {
    return put<UsageLimitRecord>(
      endpoints.platformUsageLimits.upsert,
      payload,
    );
  },

  removePlatform(id: string): Promise<void> {
    return del(endpoints.platformUsageLimits.remove(id));
  },
} as const;
